import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import * as styles from '../../styles/shared/care-numbers.module.scss';

const useIntersectionObserver = (callback) => {
  const elementRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          callback();
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1 });

    if (elementRef.current) {
      observer.observe(elementRef.current);
    }

    return () => {
      if (elementRef.current) {
        observer.unobserve(elementRef.current);
      }
    };
  }, [callback]);

  return elementRef;
};

const useCountAnimation = (endValue, duration = 1000) => {
  const [count, setCount] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  const startAnimation = () => {
    if (!isAnimating) {
      setIsAnimating(true);
      const startTime = Date.now();
      const startValue = 0;
      const endValueNum = parseInt(endValue, 10);

      const animate = () => {
        const currentTime = Date.now();
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);

        const currentValue = Math.floor(startValue + (endValueNum - startValue) * progress);
        setCount(currentValue);

        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };

      requestAnimationFrame(animate);
    }
  };

  return [count, startAnimation];
};

const CareNumbers = ({ elem }) => {
  const { title, numbers } = elem;
  const containerRef = useIntersectionObserver(() => {
    numbers.forEach((_item, index) => {
      if (startAnimations[index]) {
        startAnimations[index]();
      }
    });
  });

  const animatedValues = numbers.map(item => useCountAnimation(item.value));
  // eslint-disable-next-line no-unused-vars
  const startAnimations = animatedValues.map(([_, startAnimation]) => startAnimation);
  const currentValues = animatedValues.map(([value]) => value);

  return (
    <div
      className={styles.careNumbersContainer}
      ref={containerRef}
    >
      <div className={styles.content}>
        <h2 className={styles.title}>{title}</h2>
        <div className={styles.numbersContainer}>
          {numbers.map((item, index) => (
            <div className={styles.numberBlock}
              key={index}
            >
              <div className={styles.value}>{currentValues[index]}</div>
              <div className={styles.label}>{item.label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

CareNumbers.propTypes = {
  elem: PropTypes.shape({
    numbers: PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        label: PropTypes.string.isRequired
      })
    ).isRequired,
    title: PropTypes.string.isRequired
  }).isRequired
};

export default CareNumbers;